/**
 * Container for mutually interacting physics objects.
 * All children of a PhysicsLayer are set to collide with each other.
 * This is not mutually exclusive with other PhysicsLayers.
 * However, any two children can only have one collider across all layers.
 * @module PhysicsLayer
 * @class PhysicsLayer
 */

const PhysicsLayer = class {

    /**
     * @param {Phaser.Scene} scene - scene in which this layer will operate
     * @param {string | number} name - name which the layer will use
     */
    constructor ( scene, name ) {

        /**
         * List of objects to be removed from this layer
         * @type {Array}
         */
        this.childrenRemovable = [];

        /**
         * Whether this layer should make its members collide.
         * @type {Boolean}
         * @private
         */
        this._collideInternally = true;

        /**
         * Whether this layer needs a tidy-up.
         * @type {Boolean}
         */
        this.dirty = false;

        /**
         * List of collisions between this layer and other layers.
         * @type {Array<Phaser.Physics.Arcade.Collider>}
         */
        this.layerColliders = [];

        /**
         * Unique identifier for this layer.
         * @type {string}
         */
        this.name = (() => {
            if (name === "") {
                return "__untitled";
            }
            return name + "";
        })();

        /**
         * Level to which this layer belongs
         * @type {Phaser.Scene}
         */
        this.scene = scene;

        /**
         * Physics group which manages the layer.
         * We use a single group, because this makes the collision count
         * linear instead of quadratic, and that means performance!
         * @type {Phaser.GameObjects.Group}
         */
        this.group = scene.add.group();
    }

    /**
     * Run collision collider checks.
     * This is a callback for use in `Collider` creation.
     * It runs when two objects collide.
     * @param {Phaser.Types.Physics.Arcade.ArcadeColliderType} object1 - first colliding object
     * @param {Phaser.Types.Physics.Arcade.ArcadeColliderType} object2 - second colliding object
     * @private
     */
    _fCollideCallback ( object1, object2 ) {
        if ( object1.EVENTS && object1.EVENTS.COLLIDE ) {
            object1.emit( object1.EVENTS.COLLIDE, object2 );
        }
        if ( object2.EVENTS && object2.EVENTS.COLLIDE ) {
            object2.emit( object2.EVENTS.COLLIDE, object1 );
        }
    }

    /**
     * Run collision process checks.
     * This is a callback for use in `Collider` creation.
     * It runs to determine whether two objects should calculate collision.
     * @param {object} object1 - first colliding object
     * @param {object} object2 - second colliding object
     * @private
     * @return {Boolean}
     */
    _fProcessCallback ( object1, object2 ) {

        // Prevent an immovable object from colliding with tiles.
        // Tiles should not impede immovable objects.
        if ( object1 instanceof Phaser.Tilemaps.Tile ) {
            if ( object2.body && object2.body.immovable ) {
                return false;
            }
        } else if ( object2 instanceof Phaser.Tilemaps.Tile ) {
            if ( object1.body && object1.body.immovable ) {
                return false;
            }
        }

        return true;
    }

    /**
     * Add an entity to this layer.
     * Assign appropriate collisions and listeners.
     * @param {GameSprite} child - Entity to add
     */
    add ( child ) {
        if ( child.physicsLayer === this ) {
            return;
        }

        if ( child.physicsLayer ) {
            child.physicsLayer.remove( child );
        }

        this.group.add( child );

        child.physicsLayer = this;

        // Add collisions with the group.
        if ( this._collideInternally ) {
            this.makeCollide( child );
        }
    }

    /**
     * Destroy the colliders on a child.
     * @param {GameSprite} child - child to clean
     */
    destroyChildColliders( child ) {
        const colliders = this.getColliders( child );
        colliders.forEach( collider => {
            collider.destroy();
        } );
        child.colliders = [];
    }

    /**
     * Return the collision handler for two given entities.
     * @param {GameSprite} childA - a child in the collision
     * @param {GameSprite} childB - another child in the collision
     * @returns {Phaser.Physics.Arcade.Collider|null}
     */
    getCollider ( childA, childB ) {
        for ( let col of childA.colliders ) {
            if ( col.object1 === childB || col.object2 === childB ) {
                return col;
            }
        }
    }

    /**
     * Return a list of all colliders involving the given child.
     * @param {GameSprite} child - child to which colliders apply
     * @returns {Array}
     */
    getColliders ( child ) {
        return child.colliders;
    }

    /**
     * Return whether this layer contains the specified object.
     * @param {GameSprite} child - child to check
     * @returns {Boolean}
     */
    hasChild( child ) {
        return this.group.contains( child );
    }

    /**
     * Add a collision handler for an entity, so it interacts with the group.
     * This will be created even if such a collider already exists.
     * @param {GameSprite} child - child to collide
     */
    makeCollide ( child ) {
        if ( !child.exists ) {
            return;
        }

        // Create new collider.
        const collider = this.scene.physics.add.collider(
            child,
            this.group,
            null,
            this._fProcessCallback
        );
        child.colliders.push( collider );

        // Set up a collision event, which triggers events on both children.
        // This serves as the base for "touched" blocks.
        collider.collideCallback = this._fCollideCallback;
    }

    /**
     * Remove the specified child, and destroy all collisions
     * in which it is involved.
     * @param {GameSprite} child - child to remove
     */
    remove ( child ) {
        if ( !this.hasChild( child ) ) {
            return;
        }
        child.physicsLayer = null;
        this.childrenRemovable.push( child );
        this.destroyChildColliders( child );
        this.dirty = true;
    }

    /**
     * Remove all children and their collisions.
     */
    removeAll () {
        this.group.children.iterate( child => {
            this.remove( child );
        } );
    }

    /**
     * Set whether this layer collides with another layer.
     * @param {Number} layer - index of the target layer in this scene
     * @param {Boolean} enable - whether to collide with the target layer
     */
    setExternalCollision ( layer, enable ) {
        const targetLayer = this.scene.physicsLayersManager.getLayer(layer);
        if ( !targetLayer || targetLayer === this ) {
            return;
        }

        const cols = this.layerColliders;
        const len = cols.length;

        if ( enable ) {
            // Add the collision.

            // Are we already colliding?
            for ( let i = 0; i < len; i++ ) {
                const col = cols[ i ];
                if ( col.object1 === this || col.object2 === this ) {
                    // Don't replicate the collision.
                    return;
                }
            }
            // Create collision.
            const col = this.scene.physics.add.collider(
                this.group, targetLayer.group );
            col.collideCallback = this._fCollideCallback;
            cols.push( col );
            targetLayer.layerColliders.push( col );
        } else {
            // Remove the collision from both layers.
            let collider;
            for ( let i = 0; i < len; i++ ) {
                const col = cols[ i ];
                const groupA = this.group;
                const groupB = targetLayer.group;
                if ( col.object1 === groupA && col.object2 === groupB ) {
                    collider = col;
                    break;
                }
                if ( col.object2 === groupB && col.object1 === groupA ) {
                    collider = col;
                    break;
                }
            }
            if ( collider ) {
                collider.destroy();
                cols.splice( cols.indexOf( collider ), 1 );
                targetLayer.layerColliders.splice(
                    targetLayer.layerColliders.indexOf( collider ), 1 );
            }
        }

    }

    /**
     * Set whether this layer collides with itself.
     * If false, it will disable all collisions.
     * If true, it will create new collisions for every member.
     * @param {Boolean} enable - whether to enable collisions
     */
    setInternalCollisions ( enable ) {
        const children = this.group.children.getArray();
        if ( enable ) {
            if ( this._collideInternally ) {
                // Don't set double collisions!
                return;
            }
            this._collideInternally = true;

            children.forEach( child => {
                this.makeCollide( child );
            } );
        } else {
            if ( !this._collideInternally ) {
                // Don't double up.
                return;
            }
            this._collideInternally = false;

            children.forEach( child => {
                this.destroyChildColliders( child )
            } );
        }
    }

    /**
     * Remove all invalidated data, such as deleted game objects.
     */
    tidy () {
        if ( this.dirty ) {
            this.group.children.each( child => {
                if ( !child.exists ||
                    this.childrenRemovable.indexOf( child ) !== -1 ) {

                    this.group.children.delete( child );
                }
            } );
            this.childrenRemovable.length = 0;
            this.dirty = false;
        }
    }
};

export default PhysicsLayer;
